/**
 * Parse and serialise the `/members` URL query string.
 *
 * URL shape (deep-linkable per 04a IA / 04b wireframes):
 *
 *   /accounts
 *   /accounts?ext_account_id=AC10293849
 *   /accounts?legacy_id=1820013
 *   /accounts?ext_account_id=AC10293849&limit=100
 *
 * The cursor is intentionally NOT in the URL. Cursors are opaque,
 * HMAC-signed, and bound to the filter set (per the OpenAPI; sending a
 * cursor with a different filter set yields 400 `cursor_filter_mismatch`).
 * Putting the cursor in the URL means a deep-link to "page 7" expires
 * silently when the cursor rotates. Instead we treat the URL as the
 * filter spec and re-fetch from page 1; users can paginate from there.
 * This trades off shareable mid-pagination links (M2 could re-add via a
 * URL-stable page-token if business demands it) for correctness.
 *
 * Mutual exclusion of `ext_account_id` and `legacy_id` is enforced here:
 * if both are present, `ext_account_id` wins and `legacy_id` is dropped.
 * The upstream returns 400 `conflicting_filters` for sending both, so
 * normalising at the parse layer keeps the UI honest.
 */

export interface AccountsUrlState {
  readonly extAccountId: string | null
  readonly legacyId: string | null
  readonly limit: number
}

export const DEFAULT_LIMIT = 50
export const MAX_LIMIT = 200
export const MIN_LIMIT = 1

export function parseAccountsUrlState(search: URLSearchParams): AccountsUrlState {
  const extRaw = search.get('ext_account_id')?.trim() ?? ''
  const legacyRaw = search.get('legacy_id')?.trim() ?? ''
  const limitRaw = search.get('limit')?.trim() ?? ''

  // Mutual exclusion: ext_account_id wins if both are sent.
  const extAccountId = extRaw.length > 0 ? extRaw : null
  const legacyId =
    extAccountId === null && legacyRaw.length > 0 && isPositiveInteger(legacyRaw) ? legacyRaw : null

  let limit = Number.parseInt(limitRaw, 10)
  if (!Number.isFinite(limit) || limit < MIN_LIMIT) {
    limit = DEFAULT_LIMIT
  }
  if (limit > MAX_LIMIT) {
    limit = MAX_LIMIT
  }

  return { extAccountId, legacyId, limit }
}

export function serialiseAccountsUrlState(state: AccountsUrlState): URLSearchParams {
  const params = new URLSearchParams()
  if (state.extAccountId !== null && state.extAccountId !== '') {
    params.set('ext_account_id', state.extAccountId)
  }
  if (state.legacyId !== null && state.legacyId !== '') {
    params.set('legacy_id', state.legacyId)
  }
  if (state.limit !== DEFAULT_LIMIT) {
    params.set('limit', String(state.limit))
  }
  return params
}

export function isPositiveInteger(value: string): boolean {
  return /^[1-9][0-9]*$/.test(value)
}

/** Convenience to detect whether any filter is applied (drives empty-state copy). */
export function isFiltered(state: AccountsUrlState): boolean {
  return state.extAccountId !== null || state.legacyId !== null
}
